import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ORDER, ORDER_FORM } from "@/constants/testIds";
import AuthImage from "@/components/AuthImage";

const CATEGORIES = [
  "Apparel",
  "Apparel · Polos",
  "Apparel · Quarter Zips",
  "Apparel · Hoodies",
  "Apparel · Pullovers",
  "Apparel · Vests",
  "Apparel · Jackets",
  "Apparel · T-Shirts",
  "Apparel · Performance Wear",
  "Headwear",
  "Headcover · Driver",
  "Headcover · Fairway",
  "Headcover · Hybrid",
  "Headcover · Putter",
  "Equipment",
  "Bags & Luggage",
  "Footwear",
  "Accessories",
  "Logo Embroidery",
  "Other",
];

const ITEM_NAME_PLACEHOLDER =
  "e.g. The Motion Performance polo / The Hoppin Hoodie — leave blank if you're not sure";

// One blank item template — used for adding additional items and resetting.
const blankItem = () => ({
  // Stable per-item React key so adding/removing doesn't reorder uploaded photos
  _key: typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `i-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  item_name: "",
  description: "",
  category: "",
  color: "",
  size: "",
  quantity: 1,
  logo_preference: "",
  // Default to the first canonical logo location so submissions always
  // capture placement even when the member accepts the default without
  // opening the <select>. Backend also defaults to "Left Chest" in
  // OrderCreate, so this keeps the two sides in sync.
  logo_location: "Left Chest",
  photos: [], // [{photo_id, content_type, original_filename}]
});

export default function NewOrderPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // One ref per item for the hidden file input — keyed by _key
  const fileInputRefs = useRef({});

  const clubs = useMemo(() => user?.clubs || [], [user]);
  const defaultClubIdx = useMemo(() => {
    const home = clubs.findIndex((c) => c.is_home);
    return home >= 0 ? home : (clubs.length > 0 ? 0 : -1);
  }, [clubs]);

  const [clubIdx, setClubIdx] = useState(defaultClubIdx);
  // Fallback list matches the server-side WHERE_SEEN_OPTIONS — used while
  // the API call is in-flight and as a safety net if the call fails so the
  // dropdown is never empty.
  const FALLBACK_WHERE_SEEN = ["Online", "Home Club", "Pinehurst", "Bandon Dunes", "Pebble Beach", "Scotland", "Tournament", "Other"];
  const [whereSeenOptions, setWhereSeenOptions] = useState(FALLBACK_WHERE_SEEN);
  const [logoLocations, setLogoLocations] = useState(["Left Chest", "Left Sleeve"]);
  const [logoOptions, setLogoOptions] = useState([{ value: "Logo", label: "Logo", available: true }, { value: "No Logo", label: "No Logo", available: true }]);
  const [seedSource, setSeedSource] = useState(null); // wishlist_id we converted from

  // Shared fields — every item in this submission shares vendor + brand.
  // Vendor & brand sharing is the constraint requested by operations: a single
  // vendor packet can't address two different vendors, so we make that obvious
  // up-front instead of letting the member add a 2nd item with a conflicting brand.
  const [shared, setShared] = useState({
    brand: "",
    vendor_preference: "",  // legacy field kept on payload for shape parity; no longer captured from the form.
    where_seen: "",
    where_seen_other: "",
    notes: "",
  });
  const [items, setItems] = useState([blankItem()]);

  const [uploadingKey, setUploadingKey] = useState(null); // _key currently uploading
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [dragging, setDragging] = useState(null); // _key being dragged over

  // Pull the static option lists (Where Seen, Logo locations + Logo options)
  useEffect(() => {
    api.get("/wishlist/options").then(({ data }) => {
      if (Array.isArray(data.where_seen) && data.where_seen.length > 0) {
        setWhereSeenOptions(data.where_seen);
      }
      if (Array.isArray(data.logo_locations) && data.logo_locations.length > 0) {
        setLogoLocations(data.logo_locations);
      }
      if (Array.isArray(data.logos) && data.logos.length > 0) {
        setLogoOptions(data.logos);
      }
    }).catch(() => {});
  }, []);

  // One-shot seeding from the wishlist "Convert to Order" path.
  useEffect(() => {
    try {
      const raw = window.sessionStorage.getItem("mcc.order.seed");
      if (!raw) return;
      const seed = JSON.parse(raw);
      window.sessionStorage.removeItem("mcc.order.seed");
      setShared((s) => ({
        ...s,
        brand: seed.brand || s.brand,
        vendor_preference: seed.vendor_preference || s.vendor_preference,
        where_seen: seed.where_seen || s.where_seen,
        where_seen_other: seed.where_seen_other || s.where_seen_other,
        notes: seed.notes || s.notes,
      }));
      setItems((arr) => {
        const first = { ...arr[0] };
        if (seed.item_name) first.item_name = seed.item_name;
        if (seed.category) first.category = seed.category;
        if (seed.color) first.color = seed.color;
        if (seed.size) first.size = seed.size;
        return [first, ...arr.slice(1)];
      });
      if (seed.wishlist_source_id) setSeedSource(seed.wishlist_source_id);
    } catch (err) {
      console.warn("Could not load wishlist seed for new order:", err?.message || err);
    }
  }, []);

  const updateShared = (k) => (e) => setShared((s) => ({ ...s, [k]: e.target.value }));

  const updateItem = (key, k) => (e) => {
    const v = e.target.type === "number" ? Number(e.target.value) : e.target.value;
    setItems((arr) => arr.map((it) => (it._key === key ? { ...it, [k]: v } : it)));
  };

  const addItem = () => setItems((arr) => [...arr, blankItem()]);

  const removeItem = (key) =>
    setItems((arr) => (arr.length <= 1 ? arr : arr.filter((it) => it._key !== key)));

  const handleFiles = async (key, fileList) => {
    if (!fileList || fileList.length === 0) return;
    setError("");
    setUploadingKey(key);
    try {
      for (const file of Array.from(fileList)) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/uploads/photo", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setItems((arr) => arr.map((it) => (it._key === key ? { ...it, photos: [...it.photos, data] } : it)));
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setUploadingKey(null);
      const ref = fileInputRefs.current[key];
      if (ref) ref.value = "";
    }
  };

  const removePhoto = (key, photoId) =>
    setItems((arr) =>
      arr.map((it) =>
        it._key === key ? { ...it, photos: it.photos.filter((p) => p.photo_id !== photoId) } : it,
      ),
    );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    setSubmitting(true);
    try {
      const chosen = clubIdx >= 0 ? clubs[clubIdx] : null;
      // A single batch_id stamps every item in this submission so the back
      // office can group them on the Orders ledger.
      const batchId = items.length > 1
        ? `batch_${(typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID().slice(0, 12) : Date.now().toString(36)}`
        : null;

      const created = [];
      for (let i = 0; i < items.length; i += 1) {
        const it = items[i];
        const payload = {
          item_name: it.item_name,
          description: it.description,
          category: it.category,
          color: it.color,
          size: it.size,
          quantity: it.quantity,
          logo_preference: it.logo_preference,
          logo_location: it.logo_location,
          brand: shared.brand,
          notes: shared.notes,
          where_seen: shared.where_seen,
          where_seen_other: shared.where_seen === "Other" ? shared.where_seen_other : "",
          photo_ids: it.photos.map((p) => p.photo_id),
          club_name: chosen?.name || null,
          club_member_number: chosen?.member_number || null,
          batch_id: batchId,
        };
        // Only attach wishlist_source_id to the first item — a batch is created
        // from at most one source wishlist row.
        if (i === 0 && seedSource) payload.wishlist_source_id = seedSource;
        const { data } = await api.post("/orders", payload);
        created.push(data);
      }
      // Land on the first created order's detail page; the rest are visible in
      // the member's order history (and grouped by batch_id on the admin side).
      navigate(`/orders/${created[0].order_id}`);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-cream min-h-screen">
      <div className="max-w-5xl mx-auto px-6 sm:px-10 py-12">
        <div className="overline">New Submission</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A] mt-2 leading-none">
          Place a Special Order
        </h1>
        <p className="text-[#4A4A4A] mt-3 max-w-2xl">
          Provide the details of the item you&rsquo;d like sourced. The pro shop will review and
          confirm with the vendor on your behalf. You may add multiple items in one submission &mdash;
          all items must share the same <span className="font-semibold">Brand</span>.
        </p>

        {error && (
          <div className="mt-6 px-4 py-3 border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a] text-sm" data-testid="order-form-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="mt-10 space-y-8">
          {/* --- Shared (vendor + brand + club + where_seen + notes) --- */}
          <div className="surface-card p-8 sm:p-10">
            <div className="overline">Order Details · Shared by every item below</div>
            <h2 className="font-serif text-2xl text-[#1A1A1A] mt-2 leading-none">
              One Brand &middot; One Packet
            </h2>
            <p className="text-sm text-[#7A7A7A] mt-2 max-w-2xl">
              Every item in this submission goes on a single vendor packet, so they share these fields.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-6">
              <div className="md:col-span-2">
                <label className="label-elegant block mb-2">Brand <span className="text-[#7A7A7A] text-xs ml-1">(optional)</span></label>
                <input
                  type="text"
                  value={shared.brand}
                  onChange={updateShared("brand")}
                  data-testid="order-new-brand"
                  className="input-field"
                  placeholder="e.g. Peter Millar, Greyson"
                />
              </div>

              <div className="md:col-span-2">
                <label className="label-elegant block mb-2">For Which Club</label>
                {clubs.length === 0 ? (
                  <div className="border border-dashed border-[#B59A5A] bg-[#FBF6E8] p-4 text-sm text-[#8a6f33]">
                    You haven&rsquo;t added any clubs yet.{" "}
                    <button
                      type="button"
                      onClick={() => navigate("/profile")}
                      className="underline underline-offset-4 font-semibold"
                    >
                      Add a club on your profile →
                    </button>
                  </div>
                ) : (
                  <select
                    value={clubIdx}
                    onChange={(e) => setClubIdx(Number(e.target.value))}
                    data-testid="order-club-select"
                    className="input-field appearance-none"
                  >
                    {clubs.map((c, i) => (
                      <option key={`${c.name}-${i}`} value={i}>
                        {c.name} · #{c.member_number}{c.is_home ? " · Home" : ""}
                      </option>
                    ))}
                  </select>
                )}
                {clubIdx >= 0 && clubs[clubIdx] && (
                  <p className="text-xs text-[#7A7A7A] mt-2">
                    The pro shop will see <span className="font-mono text-[#1A422D]">{clubs[clubIdx].name} · Member #{clubs[clubIdx].member_number}</span> on this order.
                  </p>
                )}
              </div>

              <div>
                <label className="label-elegant block mb-2">Where Did You See It?</label>
                <select
                  value={shared.where_seen}
                  onChange={updateShared("where_seen")}
                  data-testid={ORDER_FORM.whereSeenSelect}
                  className="input-field appearance-none"
                >
                  <option value="">— Optional —</option>
                  {whereSeenOptions.map((w) => (
                    <option key={w} value={w}>{w}</option>
                  ))}
                </select>
              </div>
              <div>
                {shared.where_seen === "Other" ? (
                  <>
                    <label className="label-elegant block mb-2">Tell us where</label>
                    <input
                      type="text"
                      value={shared.where_seen_other}
                      onChange={updateShared("where_seen_other")}
                      data-testid={ORDER_FORM.whereSeenOtherInput}
                      className="input-field"
                      placeholder="e.g. Friend's club in Charleston"
                    />
                  </>
                ) : (
                  <div className="text-xs text-[#7A7A7A] pt-7 leading-relaxed">
                    Helps the pro shop spot trends across the membership.
                  </div>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="label-elegant block mb-2">Notes</label>
                <textarea
                  rows={4}
                  value={shared.notes}
                  onChange={updateShared("notes")}
                  data-testid={ORDER.newNotes}
                  className="input-field"
                  placeholder="Any additional details, deadlines, or preferences (shared across all items in this submission)"
                />
              </div>
            </div>
          </div>

          {/* --- Items list --- */}
          {items.map((it, idx) => (
            <div
              key={it._key}
              data-testid={`order-item-card-${idx}`}
              className="surface-card p-8 sm:p-10"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="overline">Item {idx + 1} of {items.length}</div>
                  <h3 className="font-serif text-xl text-[#1A1A1A] mt-1">
                    {it.item_name?.trim() || `Untitled item ${idx + 1}`}
                  </h3>
                </div>
                {items.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeItem(it._key)}
                    data-testid={`order-item-remove-${idx}`}
                    className="text-xs uppercase tracking-[0.18em] text-[#8a1a1a] hover:underline"
                  >
                    Remove item
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-6">
                <div className="md:col-span-2">
                  <label className="label-elegant block mb-2">Item Name <span className="text-[#7A7A7A] text-xs ml-1">(optional)</span></label>
                  <input
                    type="text"
                    value={it.item_name}
                    onChange={updateItem(it._key, "item_name")}
                    data-testid={idx === 0 ? ORDER.newItemName : `order-item-name-${idx}`}
                    className="input-field"
                    placeholder={ITEM_NAME_PLACEHOLDER}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="label-elegant block mb-2">Description <span className="text-[#7A7A7A] text-xs ml-1">(optional)</span></label>
                  <input
                    type="text"
                    value={it.description}
                    onChange={updateItem(it._key, "description")}
                    data-testid={idx === 0 ? "order-new-description" : `order-item-description-${idx}`}
                    className="input-field"
                    placeholder="e.g. Navy quarter zip, slim fit"
                  />
                </div>

                <div>
                  <label className="label-elegant block mb-2">Category</label>
                  <select
                    value={it.category}
                    onChange={updateItem(it._key, "category")}
                    data-testid={idx === 0 ? ORDER.newCategory : `order-item-category-${idx}`}
                    className="input-field appearance-none"
                  >
                    <option value="">— Select —</option>
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label-elegant block mb-2">Quantity</label>
                  <input
                    type="number"
                    min={1}
                    max={999}
                    value={it.quantity}
                    onChange={updateItem(it._key, "quantity")}
                    data-testid={idx === 0 ? ORDER.newQuantity : `order-item-quantity-${idx}`}
                    className="input-field"
                  />
                </div>

                <div>
                  <label className="label-elegant block mb-2">Color</label>
                  <input
                    type="text"
                    value={it.color}
                    onChange={updateItem(it._key, "color")}
                    data-testid={idx === 0 ? ORDER.newColor : `order-item-color-${idx}`}
                    className="input-field"
                    placeholder="e.g. Heather Forest"
                  />
                </div>
                <div>
                  <label className="label-elegant block mb-2">Size</label>
                  <input
                    type="text"
                    value={it.size}
                    onChange={updateItem(it._key, "size")}
                    data-testid={idx === 0 ? ORDER.newSize : `order-item-size-${idx}`}
                    className="input-field"
                    placeholder="e.g. Large"
                  />
                </div>

                <div>
                  <label className="label-elegant block mb-2">Logo</label>
                  <select
                    value={it.logo_preference}
                    onChange={updateItem(it._key, "logo_preference")}
                    data-testid={idx === 0 ? ORDER.newLogo : `order-item-logo-${idx}`}
                    className="input-field appearance-none"
                  >
                    <option value="">— Select —</option>
                    {logoOptions.filter((o) => o.available).map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label-elegant block mb-2">Logo location</label>
                  <select
                    value={it.logo_location}
                    onChange={updateItem(it._key, "logo_location")}
                    data-testid={idx === 0 ? "order-new-logo-location" : `order-item-logo-location-${idx}`}
                    className="input-field appearance-none"
                    disabled={it.logo_preference === "No Logo"}
                  >
                    {logoLocations.map((l) => (
                      <option key={l} value={l}>{l}</option>
                    ))}
                  </select>
                </div>

                {/* Photos & Video — per item */}
                <div className="md:col-span-2">
                  <div className="flex items-end justify-between mb-3">
                    <label className="label-elegant">Photos &amp; Video</label>
                    <span className="text-xs text-[#7A7A7A]">JPG, PNG, WEBP, MP4 · up to 50MB each</span>
                  </div>
                  <div
                    data-testid={idx === 0 ? ORDER.newPhotoDropzone : `order-item-photo-dropzone-${idx}`}
                    className={`dropzone p-8 text-center cursor-pointer ${dragging === it._key ? "is-dragging" : ""}`}
                    onClick={() => fileInputRefs.current[it._key]?.click()}
                    onDragOver={(e) => { e.preventDefault(); setDragging(it._key); }}
                    onDragLeave={() => setDragging(null)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setDragging(null);
                      handleFiles(it._key, e.dataTransfer.files);
                    }}
                  >
                    <div className="overline">{uploadingKey === it._key ? "Uploading…" : "Drop photos or video, or click to browse"}</div>
                    <p className="font-serif text-xl mt-2 text-[#1A1A1A]">Show us this piece</p>
                    <p className="text-sm text-[#4A4A4A] mt-1">Multiple files welcome.</p>
                    <input
                      ref={(el) => { fileInputRefs.current[it._key] = el; }}
                      data-testid={idx === 0 ? ORDER.newPhotoInput : `order-item-photo-input-${idx}`}
                      type="file"
                      accept="image/*,video/mp4,video/quicktime,video/webm"
                      multiple
                      hidden
                      onChange={(e) => handleFiles(it._key, e.target.files)}
                    />
                  </div>

                  {it.photos.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mt-4">
                      {it.photos.map((p) => {
                        const isVideo = (p.content_type || "").startsWith("video/");
                        return (
                          <div key={p.photo_id} className="relative group border border-[#E5E3D8] bg-white aspect-square overflow-hidden">
                            {isVideo ? (
                              <div className="w-full h-full bg-[#1A422D] text-[#F9F8F4] flex flex-col items-center justify-center text-center p-3">
                                <div className="text-3xl">▶</div>
                                <div className="overline mt-1" style={{ color: "#D9C68A" }}>Video</div>
                                <div className="text-[10px] text-white/80 mt-1 break-all">{p.original_filename}</div>
                              </div>
                            ) : (
                              <AuthImage photoId={p.photo_id} alt={p.original_filename} className="w-full h-full object-cover" />
                            )}
                            <button
                              type="button"
                              onClick={() => removePhoto(it._key, p.photo_id)}
                              className="absolute top-1 right-1 bg-[#1A422D] text-white text-[10px] tracking-widest uppercase px-2 py-1"
                            >
                              Remove
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}

          {/* Add-another control + footer */}
          <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
            <button
              type="button"
              onClick={addItem}
              data-testid="order-add-item"
              className="inline-flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-[0.18em] font-semibold border border-[#1A422D] text-[#1A422D] hover:bg-[#E9F0EB] transition-colors"
            >
              + Add another item (same vendor / brand)
            </button>
            <div className="flex items-center gap-4">
              <button type="button" onClick={() => navigate(-1)} className="btn-secondary">
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || !!uploadingKey}
                data-testid={ORDER.newSubmit}
                className="btn-primary"
              >
                {submitting
                  ? items.length > 1 ? `Submitting ${items.length} items…` : "Submitting…"
                  : items.length > 1 ? `Submit ${items.length} items` : "Submit Order"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
